/**
 * Distribution Groups Hooks
 * React Query hooks for managing distribution groups in the EdgeMail Admin Console
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { edgeMailApi as api } from '../services/EdgeMailAdminAPI';
import type {
  DistributionGroup,
  GroupMember,
  GroupType,
  MemberType,
  MemberRole,
} from '../types/admin.types';

// ============================================================================
// TYPES
// ============================================================================

interface ListGroupsParams {
  search?: string;
  groupType?: GroupType;
  isActive?: boolean;
  domainId?: string;
  sortBy?: 'display_name' | 'email' | 'created_at' | 'member_count';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

interface ListGroupsResponse {
  groups: DistributionGroup[];
  memberCounts: Record<string, number>;
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface CreateGroupData {
  domainId: string;
  localPart: string;
  displayName: string;
  description?: string;
  groupType?: GroupType;
  managedBy?: string;
  memberJoinRestriction?: 'open' | 'closed' | 'approval_required';
  memberDepartRestriction?: 'open' | 'closed' | 'approval_required';
}

interface UpdateGroupData {
  displayName?: string;
  description?: string;
  groupType?: GroupType;
  managedBy?: string;
  moderationEnabled?: boolean;
  moderators?: string[];
  bypassModerationFrom?: string[];
  deliveryRestriction?: 'none' | 'members_only' | 'approved_senders';
  approvedSenders?: string[];
  rejectedSenders?: string[];
  memberJoinRestriction?: 'open' | 'closed' | 'approval_required';
  memberDepartRestriction?: 'open' | 'closed' | 'approval_required';
  maxMessageSizeKb?: number;
  settings?: Record<string, any>;
  isActive?: boolean;
}

interface ListMembersParams {
  search?: string;
  memberType?: MemberType;
  memberRole?: MemberRole;
  page?: number;
  pageSize?: number;
}

interface ListMembersResponse {
  members: GroupMember[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface AddMemberData {
  memberId?: string;
  memberEmail: string;
  memberDisplayName?: string;
  memberType: MemberType;
  memberRole?: MemberRole;
  isExternal?: boolean;
}

export interface LocalDirectoryEntry {
  id: string;
  source: 'mailbox' | 'group';
  type: 'colleague' | 'group';
  displayName: string;
  email: string;
  initials: string;
  isInternal: true;
  groupMemberCount?: number;
  metadata: Record<string, unknown>;
}

interface LocalDirectoryResponse {
  contacts: LocalDirectoryEntry[];
  groups: LocalDirectoryEntry[];
  total: number;
  fromCache: boolean;
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const groupQueryKeys = {
  all: ['distribution-groups'] as const,
  lists: () => [...groupQueryKeys.all, 'list'] as const,
  list: (params: ListGroupsParams) => [...groupQueryKeys.lists(), params] as const,
  details: () => [...groupQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...groupQueryKeys.details(), id] as const,
  members: (groupId: string) => [...groupQueryKeys.all, 'members', groupId] as const,
  memberList: (groupId: string, params: ListMembersParams) =>
    [...groupQueryKeys.members(groupId), params] as const,
  directory: (search: string, scope: string, limit: number) =>
    [...groupQueryKeys.all, 'local-directory', search, scope, limit] as const,
};

export function useLocalDirectorySearch(params: { search?: string; scope?: 'colleagues' | 'groups' | 'all'; limit?: number } = {}) {
  const search = params.search?.trim() || '';
  const scope = params.scope || 'all';
  const limit = params.limit || 25;

  return useQuery({
    queryKey: groupQueryKeys.directory(search, scope, limit),
    queryFn: async (): Promise<LocalDirectoryResponse> => {
      const response = await api.get('/directory/search', {
        params: { q: search, scope, limit },
      });
      return response.data;
    },
    staleTime: 30000,
  });
}

// ============================================================================
// HOOKS - GROUP MANAGEMENT
// ============================================================================

/**
 * Hook to fetch paginated list of distribution groups
 */
export function useDistributionGroupList(params: ListGroupsParams = {}) {
  const queryParams = new URLSearchParams();

  if (params.search) queryParams.set('search', params.search);
  if (params.groupType) queryParams.set('groupType', params.groupType);
  if (params.isActive !== undefined) queryParams.set('isActive', String(params.isActive));
  if (params.domainId) queryParams.set('domainId', params.domainId);
  if (params.sortBy) queryParams.set('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);
  if (params.page) queryParams.set('page', String(params.page));
  if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));

  return useQuery({
    queryKey: groupQueryKeys.list(params),
    queryFn: async (): Promise<ListGroupsResponse> => {
      const response = await api.get(`/admin/groups?${queryParams.toString()}`);
      return response.data;
    },
  });
}

/**
 * Hook to fetch a single distribution group by ID
 */
export function useDistributionGroup(groupId: string | null) {
  return useQuery({
    queryKey: groupQueryKeys.detail(groupId || ''),
    queryFn: async (): Promise<DistributionGroup & { memberCount: number }> => {
      const response = await api.get(`/admin/groups/${groupId}`);
      return response.data.group;
    },
    enabled: !!groupId,
  });
}

/**
 * Hook to create a new distribution group
 */
export function useCreateDistributionGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateGroupData): Promise<DistributionGroup> => {
      const response = await api.post('/admin/groups', data);
      return response.data.group;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupQueryKeys.lists() });
    },
  });
}

/**
 * Hook to update a distribution group
 */
export function useUpdateDistributionGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      data,
    }: {
      groupId: string;
      data: UpdateGroupData;
    }): Promise<DistributionGroup> => {
      const response = await api.patch(`/admin/groups/${groupId}`, data);
      return response.data.group;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: groupQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: groupQueryKeys.detail(variables.groupId) });
    },
  });
}

/**
 * Hook to delete a distribution group
 */
export function useDeleteDistributionGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (groupId: string): Promise<void> => {
      await api.delete(`/admin/groups/${groupId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: groupQueryKeys.lists() });
    },
  });
}

// ============================================================================
// HOOKS - MEMBER MANAGEMENT
// ============================================================================

/**
 * Hook to fetch members of a distribution group
 */
export function useGroupMembers(groupId: string | null, params: ListMembersParams = {}) {
  const queryParams = new URLSearchParams();

  if (params.search) queryParams.set('search', params.search);
  if (params.memberType) queryParams.set('memberType', params.memberType);
  if (params.memberRole) queryParams.set('memberRole', params.memberRole);
  if (params.page) queryParams.set('page', String(params.page));
  if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));

  return useQuery({
    queryKey: groupQueryKeys.memberList(groupId || '', params),
    queryFn: async (): Promise<ListMembersResponse> => {
      const response = await api.get(
        `/admin/groups/${groupId}/members?${queryParams.toString()}`
      );
      return response.data;
    },
    enabled: !!groupId,
  });
}

/**
 * Hook to add a member to a distribution group
 */
export function useAddGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      data,
    }: {
      groupId: string;
      data: AddMemberData;
    }): Promise<GroupMember> => {
      const response = await api.post(`/admin/groups/${groupId}/members`, data);
      return response.data.member;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: groupQueryKeys.members(variables.groupId) });
      queryClient.invalidateQueries({ queryKey: groupQueryKeys.detail(variables.groupId) });
    },
  });
}

/**
 * Hook to remove a member from a distribution group
 */
export function useRemoveGroupMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      membershipId,
    }: {
      groupId: string;
      membershipId: string;
    }): Promise<void> => {
      await api.delete(`/admin/groups/${groupId}/members/${membershipId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: groupQueryKeys.members(variables.groupId) });
      queryClient.invalidateQueries({ queryKey: groupQueryKeys.detail(variables.groupId) });
    },
  });
}

/**
 * Hook to update a group member's role
 */
export function useUpdateGroupMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      membershipId,
      memberRole,
    }: {
      groupId: string;
      membershipId: string;
      memberRole: MemberRole;
    }): Promise<GroupMember> => {
      const response = await api.patch(
        `/admin/groups/${groupId}/members/${membershipId}`,
        { memberRole }
      );
      return response.data.member;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: groupQueryKeys.members(variables.groupId) });
    },
  });
}
